/**
 * payments.service.ts
 *
 * Typed API call wrappers for the payments domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see raw Axios responses.
 *
 * SECURITY: Raw card numbers are never passed through this layer.
 * Only gateway-issued tokens and nonces flow here.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Re-exported domain types (mirrors server payments.types.ts) ──────────────

export interface Payment {
  paymentID:         number;
  planID:            number | null;
  patientID:         number | null;
  quoteID:           number | null;
  accountID:         number;
  userID:            number | null;
  dtmDate:           string;
  strPayType:        string | null;
  strPayMethod:      string | null;
  strRefNumber:      string | null;
  curAmount:         number;
  strMemo:           string | null;
  materialsText:     string | null;
  entryDate:         string | null;
  void:              boolean | '0' | '1';
  voidDate:          string | null;
  voidUserID:        number | null;
  /** Resolved name of the user who voided (null unless voided). Populated by
   *  the single-payment detail endpoint; list surfaces resolve it client-side. */
  voidUserName?:     string | null;
  doctorID:          number | null;
  coordID:           number | null;
  planDisplayID:     string | null;
  patientFirstName:  string | null;
  patientLastName:   string | null;
  processor:         ProcessorType | null;
  enteringUserName:  string | null;
  paymentsCreditID?: number | null;
  paymentMethodID?: number | null;
  facilityID?: number | null;
  facilityName?: string | null;
  materialsCategoryText?: string | null;
  discount?: number | null;
  /**
   * Latest gateway transaction status pulled from
   * `payment_transactions_authnet.status` (the legacy `getTransStatus()`
   * column). Raw values: `pending`, `declined`, `settledSuccessfully`,
   * `capturedPendingSettlement`, `refundPendingSettlement`,
   * `refundSettledSuccessfully`, `voided`, or null/empty for manual entries.
   * Map through `paymentStatus.ts` for display labels.
   */
  transStatus?:      string | null;
  giftCardTotal?:    number;
  /**
   * Amount actually charged to a card, resolved server-side. Prefer this over
   * `curAmount` for display: `curAmount` is net of an applied gift card on some
   * payments and gross on others, so showing it raw overstates the charge on
   * every gross-convention row.
   */
  cardChargedAmount?: number;
  /** Pre-tender total (charged + gift card). */
  grossAmount?:      number;
  displayDate?:      string | null;
}

export interface PaymentMethodOption {
  paymentMethodID: number;
  name: string;
  pmEnumID: number | null;
  typeIntentID: number | null;
  active: boolean;
  isCreditType: boolean;
  /**
   * Configured credit-card surcharge for this method (payment_methods_surcharges),
   * or null when none is set. Display only — the backend re-computes and folds
   * the surcharge authoritatively at charge time. `'flat'` = a dollar amount,
   * `'percent'` = a percentage of the charged base.
   */
  surchargeType: 'flat' | 'percent' | null;
  surchargeAmount: number | null;
}

export interface PaymentDetailTransaction {
  paymentTransactionAuthnetID: number;
  paymentMethodToken: string | null;
  transactionID: string | null;
  status: string | null;
  amount: number;
  createDate: string | null;
  accountAuthnetID: number | null;
  /** Name of the merchant account this attempt was charged to (inactive merchants included). */
  merchantTitle: string | null;
  /** Why a declined attempt was declined, as the card processor reported it. Null when not declined or unknown. */
  declineReason?: string | null;
}

export interface PaymentDetailCredit {
  paymentsCreditID: number;
  serialNumber: string | null;
  creditAmount: number;
  redeemedAmount: number;
  creditTypeName: string | null;
}

export interface PaymentDetailGiftCard {
  giftCardLogID: number;
  giftCardID: number;
  serialNumber: string | null;
  title: string | null;
  amount: number;
  balanceAmount: number;
  redeemedAmount: number;
  createDate: string | null;
}

export interface PaymentDetailMaterial {
  paymentMaterialID: number;
  itemId: number;
  name: string | null;
  description: string | null;
  price: number;
  taxable: boolean;
  quantity: number;
  salesTax: number;
  discount1Id: number | null;
  discount2Id: number | null;
  manualDiscount1: number;
  manualDiscount2: number;
  total: number;
  categoryTitle: string | null;
  /**
   * For gift-card SKU lines: the serial of the card this line issued. Masked
   * on an unclaimed online card for users without the Gift Credit permission,
   * and null once an online card is claimed (the owner is shown instead, R22).
   */
  serialNumber: string | null;
  /** The card this line issued, when it is a gift-card SKU line. */
  giftCardId?: number | null;
  giftCardCodeMasked?: boolean;
  /** An online card the recipient has claimed: show the owner, not the serial. */
  giftCardClaimed?: boolean;
  giftCardOwnerPatientId?: number | null;
  giftCardOwnerName?: string | null;
}

export interface PaymentDetailLoan {
  paymentLoanID: number;
  loanId: number;
  paymentAmount: number;
  loanType: string | null;
  loanAmount: number;
  loanStatus: string | null;
  createDate: string | null;
}

export interface PaymentDetailPackageItem {
  itemName: string;
  quantity: number;
  itemType: 'product' | 'procedure';
}

export interface PaymentDetailPackage {
  packageId: number;
  title: string | null;
  packageTotal: number;
  templatePrice: number;
  credit: number;
  /** This package's share of the payment's card charge (pro-rata by list price). */
  chargeAmount: number;
  /** This package's share of the payment's gift card. Never the payment total. */
  giftCardApplied: number;
  discount1Id: number | null;
  discount2Id: number | null;
  manualDiscount1: number;
  manualDiscount2: number;
  createDate: string | null;
  items: PaymentDetailPackageItem[];
}

export interface PaymentDetailMembership {
  enrollmentId: number;
  title: string | null;
  recurringAmount: number;
  createDate: string | null;
  paymentDate: string | null;
  paymentUntilDate: string | null;
}

export interface PaymentDetailPromoCode {
  promoCodeID: number;
  code: string;
  title: string;
  description: string;
  type: string;
  discountTitle: string;
  discountType: number;
  discountAmount: number;
  appliedDiscount: number;
}

export interface PaymentDetail extends Payment {
  /**
   * Server-resolved money figures. `payments.curAmount` is net of an applied
   * gift card when written by PatientPaymentService, but gross when written by
   * the POS gift-card tender or a standalone GiftCardsController::redeem — only
   * the API can tell which, so it resolves them and the UI renders them as-is.
   */
  /** Value of what was sold (packages, else memberships, else product lines). */
  productTotal: number;
  grossAmount: number;
  cardChargedAmount: number;
  giftCardAppliedTotal: number;
  curAmountIsGross: boolean;
  transactions: PaymentDetailTransaction[];
  credits: PaymentDetailCredit[];
  /** Every gift card ledger row on the payment, restores (negative) included. */
  giftCards: PaymentDetailGiftCard[];
  /**
   * What the payment asks of each card, one entry per card: its net on a live
   * payment, or the share a declined / voided payment holds for a retry or
   * un-void. The edit form starts from this, so saving it unchanged spends
   * and restores nothing.
   */
  appliedGiftCards?: Array<{ giftCardID: number; amount: number }>;
  materials: PaymentDetailMaterial[];
  loans: PaymentDetailLoan[];
  packages: PaymentDetailPackage[];
  memberships: PaymentDetailMembership[];
  promoCode: PaymentDetailPromoCode | null;
  /**
   * Shipping charged on this payment, one entry per distinct fee. NOT part of
   * `materials` or `productTotal` (shipping is not a product line) but folded
   * into `curAmount` / `grossAmount`. The edit modal seeds from this so
   * reopening a payment neither loses the shipping nor re-derives and
   * duplicates it. `name` and `amount` are snapshots taken at charge time.
   */
  shipping: Array<{ shippingFeeID: number; name: string; amount: number }>;
  shippingTotal: number;
}

/**
 * Payment processor identifier. Forward is the only live gateway; `Manual`
 * is the bookkeeping label for cash / check / gift-card / write-in entries
 * that don't hit a processor at all. AuthNet, Braintree, and Spreedly were
 * dropped during the V5 Forward-only consolidation.
 */
export enum ProcessorType {
  Forward = 'forward',
  Manual  = 'manual',
}

export interface TransactionResult {
  payment:     Payment;
  transaction: PaymentTransaction;
}

export interface PaymentTransaction {
  paymentTransactionID:  number;
  patientID:             number | null;
  planID:                number | null;
  quoteID:               number | null;
  paymentID:             number | null;
  paymentMethodToken:    string | null;
  transactionID:         string | null;
  status:                string | null;
  amount:                number | null;
  createDate:            string | null;
  accountID:             number;
}

export interface ForwardPaymentIntent {
  client_secret: string | null;
  id: string | null;
  amount: number;
  status: string;
  public_api_key: string | null;
  merchant_id: number | null;
  forward_gateway_account_id: string | null;
}

export interface RecurringSchedule {
  paymentsRecurringID:  number;
  paymentID:            number | null;
  patientID:            number | null;
  planID:               number | null;
  accountID:            number;
  paymentMethodToken:   string | null;
  incrementType:        RecurringFrequency | null;
  chargeDay:            number | null;
  createDate:           string | null;
  endDate:              string | null;
  curAmount:            number | null;
  strPayType:           string | null;
  strPayMethod:         string | null;
  strMemo:              string | null;
  recurringId?:         number;
  patientId?:           number | null;
  chargeNow?:           boolean;
  chargeResult?: {
    recurringId: number;
    recordId?: number;
    status: string;
    paymentId: number;
    paymentTransactionAuthnetId?: number;
  };
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  const parsed = String(value).trim();
  return parsed === '' ? null : parsed;
}

function mapPayment(raw: Record<string, unknown>): Payment {
  return {
    paymentID: asNullableNumber(raw.paymentID) ?? 0,
    planID: asNullableNumber(raw.planID),
    patientID: asNullableNumber(raw.patientID),
    quoteID: asNullableNumber(raw.quoteID),
    accountID: asNullableNumber(raw.accountID) ?? 0,
    userID: asNullableNumber(raw.userID),
    dtmDate: asNullableString(raw.dtmDate) ?? '',
    strPayType: asNullableString(raw.strPayType),
    strPayMethod: asNullableString(raw.strPayMethod),
    strRefNumber: asNullableString(raw.strRefNumber),
    curAmount: asNullableNumber(raw.curAmount) ?? 0,
    strMemo: asNullableString(raw.strMemo),
    materialsText: asNullableString(raw.materialsText),
    entryDate: asNullableString(raw.entryDate),
    void: raw.void === true || raw.void === 1 || raw.void === '1' ? '1' : '0',
    voidDate: asNullableString(raw.voidDate),
    voidUserID: asNullableNumber(raw.voidUserID),
    voidUserName: asNullableString(raw.voidUserName),
    doctorID: asNullableNumber(raw.doctorID),
    coordID: asNullableNumber(raw.coordID),
    planDisplayID: asNullableString(raw.planDisplayID),
    patientFirstName: asNullableString(raw.patientFirstName),
    patientLastName: asNullableString(raw.patientLastName),
    processor: asNullableString(raw.processor) as ProcessorType | null,
    enteringUserName: asNullableString(raw.enteringUserName),
    paymentsCreditID: asNullableNumber(raw.paymentsCreditID),
    paymentMethodID: asNullableNumber(raw.paymentMethodID),
    facilityID: asNullableNumber(raw.facilityID),
    materialsCategoryText: asNullableString(raw.materialsCategoryText),
    discount: asNullableNumber(raw.discount),
  };
}

function mapBillingMethod(raw: Record<string, unknown>): BillingMethod {
  const processorRaw = String(raw.processor ?? '').trim().toLowerCase();
  // Forward-only: anything not explicitly tagged "manual" is treated as
  // a Forward-backed token. Legacy AuthNet/Braintree/Spreedly rows still
  // in the DB will surface as Forward — they will fail at charge time
  // because their tokens aren't valid Forward payment_method ids; that
  // surfaces as an explicit decline rather than silently routing to a
  // non-existent processor handler.
  const processor: ProcessorType = processorRaw === 'manual'
    ? ProcessorType.Manual
    : ProcessorType.Forward;

  return {
    billingMethodID: asNullableNumber(raw.billingMethodID ?? raw.patientsAuthnetID ?? raw.id) ?? 0,
    patientID: asNullableNumber(raw.patientID) ?? 0,
    accountID: asNullableNumber(raw.accountID) ?? 0,
    processor,
    token: asNullableString(raw.token ?? raw.paymentMethodToken) ?? '',
    paymentProfileId: asNullableString(raw.paymentProfileId ?? raw.paymentProfileID),
    customerProfileId: asNullableString(raw.customerProfileId ?? raw.customerID),
    title: asNullableString(raw.title) ?? '',
    lastFour: asNullableString(raw.lastFour ?? raw.lastfour),
    firstSix: asNullableString(raw.firstSix ?? raw.firstsix),
    expireDate: asNullableString(raw.expireDate),
    isDefault: Boolean(raw.isDefault ?? false),
    createDate: asNullableString(raw.createDate),
    endDate: asNullableString(raw.endDate),
    pmEnumID: asNullableNumber(raw.pmEnumID),
    paymentMethodID: asNullableNumber(raw.paymentMethodID),
    accountAuthnetID: asNullableNumber(raw.accountAuthnetID),
  };
}

function mapRecurringSchedule(raw: Record<string, unknown>): RecurringSchedule {
  const recurringId =
    asNullableNumber(raw.paymentsRecurringID) ??
    asNullableNumber(raw.paymentsRecurringAuthnetID) ??
    asNullableNumber(raw.recurringId) ??
    0;
  const patientId = asNullableNumber(raw.patientID) ?? asNullableNumber(raw.patientId);
  const chargeResultRaw = raw.chargeResult as Record<string, unknown> | undefined;

  return {
    paymentsRecurringID: recurringId,
    paymentID: asNullableNumber(raw.paymentID),
    patientID: patientId,
    planID: asNullableNumber(raw.planID),
    accountID: asNullableNumber(raw.accountID) ?? 0,
    paymentMethodToken: asNullableString(raw.paymentMethodToken),
    incrementType: (asNullableString(raw.incrementType) as RecurringFrequency | null) ?? null,
    chargeDay: asNullableNumber(raw.chargeDay),
    createDate: asNullableString(raw.createDate),
    endDate: asNullableString(raw.endDate),
    curAmount: asNullableNumber(raw.curAmount),
    strPayType: asNullableString(raw.strPayType),
    strPayMethod: asNullableString(raw.strPayMethod),
    strMemo: asNullableString(raw.strMemo),
    recurringId,
    patientId,
    chargeNow: raw.chargeNow === true || raw.chargeNow === 1 || raw.chargeNow === '1',
    chargeResult: chargeResultRaw
      ? {
          recurringId: asNullableNumber(chargeResultRaw.recurringId) ?? recurringId,
          recordId: asNullableNumber(chargeResultRaw.recordId) ?? undefined,
          status: asNullableString(chargeResultRaw.status) ?? '',
          paymentId: asNullableNumber(chargeResultRaw.paymentId) ?? 0,
          paymentTransactionAuthnetId: asNullableNumber(chargeResultRaw.paymentTransactionAuthnetId) ?? undefined,
        }
      : undefined,
  };
}

export enum RecurringFrequency {
  Weekly    = 'weekly',
  Biweekly  = 'biweekly',
  Monthly   = 'monthly',
}

export interface BillingMethod {
  billingMethodID:     number;
  patientID:           number;
  accountID:           number;
  processor:           ProcessorType;
  /** Gateway-issued token — never a raw card number */
  token:               string;
  paymentProfileId:    string | null;
  customerProfileId:   string | null;
  title:               string;
  lastFour:            string | null;
  firstSix?:           string | null;
  expireDate:          string | null;
  isDefault:           boolean;
  createDate:          string | null;
  /** Non-null when soft-deleted (datetime the method was deactivated) */
  endDate:             string | null;
  pmEnumID?:           number | null;
  paymentMethodID?:    number | null;
  accountAuthnetID?:   number | null;
}

export interface Credit {
  paymentsCreditID:      number;
  accountID:             number;
  patientID:             number | null;
  planID:                number | null;
  paymentsCreditTypeID:  number | null;
  serialNumber:          string | null;
  creditAmount:          number;
  redeemedAmount:        number;
  paymentType:           string | null;
  void:                  '0' | '1';
  createDate:            string | null;
  creditTypeName:        string | null;
}

export interface GiftCard {
  giftCardId?:       number;
  paymentsCreditID:  number;
  accountID:         number;
  serialNumber:      string;
  creditAmount:      number;
  redeemedAmount:    number;
  remainingBalance:  number;
  currentBalance?:   number;
  totalValue?:       number;
  title?:            string | null;
  void:              '0' | '1';
  createDate:        string | null;
  creditTypeName:    string | null;
}

export interface QuoteLineItem {
  quoteLineItemID:  number;
  quoteID:          number;
  description:      string;
  quantity:         number;
  unitPrice:        number;
  totalPrice:       number;
  procedureID:      number | null;
}

export interface Quote {
  quoteID:        number;
  planID:         number | null;
  patientID:      number | null;
  accountID:      number;
  totalAmount:    number | null;
  depositAmount:  number | null;
  status:         QuoteStatus;
  signedDate:     string | null;
  signedByName:   string | null;
  createDate:     string | null;
  expiresDate:    string | null;
  notes:          string | null;
  lineItems:      QuoteLineItem[];
}

export enum QuoteStatus {
  Draft   = 'draft',
  Sent    = 'sent',
  Signed  = 'signed',
  Expired = 'expired',
  Voided  = 'voided',
}

// ─── Request payload types ────────────────────────────────────────────────────

export interface PaymentFilters {
  patientId?:   number;
  /** Exact-match jump to one payment (e.g. a hyperlink from App Orders — SCRUM-1868). */
  paymentID?:   number;
  /** One plan's payments (payments.planID), for a plan's Payments card. */
  planId?:      number;
  facilityId?:  number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  doctorID?:    number;
  doctorIDs?:   number[];
  doctorIDsNegate?: boolean;
  coordID?:     number;
  coordIDs?:    number[];
  coordIDsNegate?: boolean;
  dateFrom?:    string;
  dateTo?:      string;
  status?:      string;
  statuses?:    string[];
  statusesNegate?: boolean;
  processor?:   ProcessorType;
  type?:        string;
  search?:      string;
  /** Server expects `paymentType`; keep `type` alias for back-compat. */
  paymentType?: string;
  paymentTypes?: string[];
  paymentTypesNegate?: boolean;
  productIds?: number[];
  productIdsNegate?: boolean;
  planTypeIds?: number[];
  planTypeIdsNegate?: boolean;
  membershipIds?: number[];
  membershipIdsNegate?: boolean;
  packageIds?: number[];
  packageIdsNegate?: boolean;
  userId?: number;
  userIds?: number[];
  userIdsNegate?: boolean;
}

export interface PaymentPagination {
  page?:     number;
  pageSize?: number;
  export?:   boolean;
  sortBy?:   string;
  sortDir?:  'asc' | 'desc';
}

export interface CreateRecurringPayload {
  patientID:          number;
  planID?:            number;
  quoteID?:           number;
  facilityID?:        number;
  paymentMethodID?:   number;
  paymentMethodToken: string;
  incrementType:      RecurringFrequency | string | number;
  chargeDay:          number;
  startDate?:         string;
  endDate?:           string;
  curAmount:          number;
  strPayType?:        string;
  strPayMethod?:      string;
  strMemo?:           string;
  accountMerchantId?: string;
  membershipTemplateIds?: number[];
  giftCardIds?: number[];
  loanIds?: number[];
  chargeNow?: boolean;
}

export type UpdateRecurringPayload = Partial<CreateRecurringPayload>;

export interface SaveBillingMethodPayload {
  patientId:          number;
  paymentMethodToken: string;
  title:              string;
  cardType?:          string;
  lastfour?:          string;
  firstsix?:          string;
  expireDate?:        string;
  customerID?:        string;
  processorEnumID?:   number;
  pmEnumID?:          number;
  zipcode?:           string;
  accountAuthnetID?:  number;
}

export interface CreatePatientPaymentPayload {
  amount: number;
  date: string;
  method: string;
  strPayType?: string;
  memo?: string;
  referenceNumber?: string;
  paymentID?: number;
  paymentMethodID?: number;
  paymentMethodToken?: string;
  accountAuthnetID?: number;
  planID?: number;
  quoteID?: number;
  facilityID?: number;
  selectedCreditId?: number;
  creditAmount?: number;
  chargeAmount?: number;
  gatewayTransactionId?: string;
  gatewayStatus?: string;
  /**
   * Forward payment_intent id (pi_*). Carried separately from
   * `gatewayTransactionId` (which holds the Forward payment id, pmt_*).
   * When present the backend writes a `payment_transactions_intents`
   * bridge row so the detail-modal status normalizer and refund endpoint
   * can resolve PTA → intent. Required for the terminal-checkout flow
   * (legacy parity: payment_process_charge_authnet.php:47-48 + 479-488).
   */
  paymentIntentId?: string;
  giftCards?: Array<{
    giftCardId: number;
    amount: number;
  }>;
  loans?: Array<{
    paymentLoanID?: number;
    loanId: number;
  }>;
  /**
   * Shipping fees applied to this payment — IDs only, one entry per distinct
   * fee. IMPORTANT: `amount` above is the PRE-shipping base. The backend reads
   * each fee's dollar amount from `shipping_fees` and folds it into
   * `payments.curAmount`, so sending a shipping-inclusive `amount` here would
   * double-charge it.
   */
  shipping?: Array<{
    shippingFeeId: number;
  }>;
  /**
   * When the payment is being raised against a scheduled appointment, the
   * caller passes the appointment's id so the backend can update the SAP
   * (`scheduled_appointment_products`) rows post-charge. Mirrors the
   * `apptID` argument used by legacy `payment-consume-product2.php`.
   */
  appointmentId?: number;
  items?: Array<{
    paymentMaterialID?: number;
    itemId: number;
    /**
     * Per-line display name override. Integration checkouts (Axiom, GetLabs,
     * SiPhox, …) share ONE generic catalog row per module, so without this
     * every line would persist as the module's generic catalog name (e.g.
     * "Axiom (Integration)") instead of the actual peptide/pen/kit ordered.
     * Omit to use the catalog item's own name (all non-integration callers).
     */
    name?: string;
    description?: string;
    price?: number;
    postDiscountTotal?: number;
    taxable?: boolean;
    quantity?: number;
    salesTax?: number;
    discount1Id?: number;
    discount2Id?: number;
    manualDiscount1?: number;
    manualDiscount2?: number;
    /** Linkage back to a pending `scheduled_appointment_products` row. */
    scheduledAppointmentProductsId?: number;
    /**
     * When the prefill row was grouped across N pending slots for the
     * same product type, every SAP id is supplied here. The backend
     * creates one `payment_materials` row for the items entry and then
     * links every SAP id to that single material so all N slots flip
     * from `pending` to `payment` in a single round-trip.
     */
    scheduledAppointmentProductsIds?: number[];
    /**
     * Gift-card SKU fields (legacy `payment_processor.php` 737-884). When
     * `productType=1` the API issues (or tops up) a `gift_cards` row and
     * links it to the line via `gift_cards_products`.
     */
    productType?: number;
    giftCardId?: number;
    creditValue?: number;
    serialNumber?: string;
    giftCardTitle?: string;
    targetGiftCardPatientId?: number;
  }>;
  /**
   * Set `true` by the ad-hoc Charge Payment flow so the payment trigger is
   * NOT fired during Phase 1. Phase 2 (`chargeForwardPayment`) runs the
   * trigger after the gateway confirms the capture — legacy parity with
   * `payment_process_charge_authnet.php` which only invokes TaskProcessor
   * when `$void == '0'`.
   */
  deferPaymentTrigger?: boolean;
}

export interface CreateQuotePayload {
  planID?:        number;
  patientID:      number;
  totalAmount?:   number;
  depositAmount?: number;
  expiresDate?:   string;
  notes?:         string;
  lineItems:      Array<{
    description:  string;
    quantity:     number;
    unitPrice:    number;
    procedureID?: number;
  }>;
}

// ─── Service methods ──────────────────────────────────────────────────────────

export interface UnifiedPaymentParams {
  filtersArray?: string;
  search?: string;
  patientId?: number;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  export?: boolean;
}

/**
 * Fetch a paginated list of payments with optional filters.
 * Accepts either the unified filtersArray format or legacy per-param format.
 */
export function getPayments(
  filters: PaymentFilters | UnifiedPaymentParams = {},
  pagination: PaymentPagination = {},
): Promise<PaginatedResponse<Payment>> {
  const { sortBy, sortDir, ...paginationRest } = pagination;

  const sortKeyMap: Record<string, string> = {
    dtmDate: 'date',
    curAmount: 'amount',
    patientID: 'patientName',
  };

  const params: Record<string, unknown> = { ...filters, ...paginationRest };
  if (sortBy && sortKeyMap[sortBy]) {
    params.sort = sortKeyMap[sortBy];
    params.order = sortDir ?? 'desc';
  }

  return apiClient
    .get<PaginatedResponse<Payment>>('/payments', { params })
    .then((r) => r.data);
}

// ─── Product Allocations ──────────────────────────────────────────────────────

/**
 * One product allocation — a single `payment_materials` line, or the membership
 * behind a recurring charge that has no product lines. Ports the legacy
 * "Product Allocations" grid (payment_credit_list-test.php?pageID=4).
 */
export interface AllocationRow {
  /** Surrogate key spanning both grain branches; unique per row. */
  rowKey: number;
  paymentID: number;
  patientID: number | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  displayDate: string | null;
  /** Payment type, already carrying the `Recurring (next …)` override. */
  typeLabel: string | null;
  strPayMethod: string | null;
  /** This line's amount — NOT the payment total. */
  allocationAmount: number;
  /**
   * Units purchased on this line. A row is one line as PURCHASED: the server
   * collapses the rows a partial disbursement splits it into, and sums their
   * quantity back here.
   */
  quantity: number;
  /** Raw `payments_list_index.paymentStatus`, for PaymentStatusBadge. */
  transStatus: string | null;
  void: '0' | '1';
  voidDate: string | null;
  product: string | null;
  category: string | null;
  facilityName: string | null;
  strMemo: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  voidUserFirstName: string | null;
  voidUserLastName: string | null;
  enteredDate: string;
}

/** Allocation amounts summed per product category (legacy's Category view). */
export interface AllocationCategoryRow {
  categoryId: number | null;
  category: string;
  allocations: number;
  totalAmount: number;
  giftCredits: number;
}

/** `meta` carries the summed allocation amount for the page-header total. */
export interface AllocationsMeta {
  total: number;
  page: number;
  perPage: number;
  allocationTotal: number;
}

export interface AllocationsResponse {
  data: AllocationRow[];
  meta: AllocationsMeta;
}

/** The category view is not paginated, so its meta omits page/perPage. */
export interface AllocationCategoriesResponse {
  data: AllocationCategoryRow[];
  meta: { total: number; allocationTotal: number };
}

/**
 * UI sort keys map onto the API's named keys, which the backend resolves via
 * PaymentAllocationsService::SORTABLE. Every one of the 10 grid columns is
 * sortable — legacy could only sort 9 of them, and mis-wired the last four.
 */
const ALLOCATION_SORT_KEYS: Record<string, string> = {
  displayDate: 'date',
  patientID: 'patient',
  typeLabel: 'type',
  allocationAmount: 'amount',
  transStatus: 'status',
  product: 'product',
  category: 'category',
  facilityName: 'facility',
  strMemo: 'notes',
  userLastName: 'user',
};

export function getAllocations(
  filters: UnifiedPaymentParams = {},
  pagination: PaymentPagination = {},
): Promise<AllocationsResponse> {
  const { sortBy, sortDir, ...paginationRest } = pagination;

  const params: Record<string, unknown> = { ...filters, ...paginationRest };
  if (sortBy && ALLOCATION_SORT_KEYS[sortBy]) {
    params.sort = ALLOCATION_SORT_KEYS[sortBy];
    params.order = sortDir ?? 'desc';
  }

  return apiClient
    .get<AllocationsResponse>('/payments/allocations', { params })
    .then((r) => r.data);
}

export function getAllocationCategories(
  filters: UnifiedPaymentParams = {},
): Promise<AllocationCategoriesResponse> {
  return apiClient
    .get<AllocationCategoriesResponse>('/payments/allocations/categories', {
      params: filters,
    })
    .then((r) => r.data);
}

/**
 * Fetch a single payment record by ID.
 */
export function getPayment(id: number): Promise<Payment> {
  return apiClient
    .get<ApiResponse<Payment>>(`/payments/${id}`)
    .then((r) => r.data.data);
}

/**
 * Record a manual payment (cash, check, etc.).
 */
export function createPayment(data: Partial<Payment>): Promise<Payment> {
  return apiClient
    .post<ApiResponse<Payment>>('/payments', data)
    .then((r) => r.data.data);
}

/**
 * Void a payment record. Returns no data on success.
 */
export function voidPayment(id: number): Promise<void> {
  return apiClient.patch(`/payments/${id}/void`).then(() => undefined);
}

/**
 * Per-row Void for a single Forward PTA. Distinct from `voidPayment` above
 * (which is the toolbar Void / per-payment local-only flip). This endpoint
 * calls Forward `cancel_payment_intent`; on cancel failure it falls back
 * to a FULL refund, then flips the parent payment to void=1. Mirrors
 * legacy `update_payment_transaction_authnet.php:171-241` (the per-row
 * Action-button POST in the legacy payment detail modal).
 */
export function voidPaymentTransaction(transactionId: number): Promise<{
  success: boolean;
  method: 'cancel' | 'refund';
  paymentID: number;
}> {
  return apiClient
    .post<ApiResponse<{ success: boolean; method: 'cancel' | 'refund'; paymentID: number }>>(
      `/payments/transactions/${transactionId}/void`,
    )
    .then((r) => r.data.data);
}

/**
 * Un-void a previously voided payment (legacy parity — `voidPayment` toggle).
 */
export function unvoidPayment(id: number): Promise<void> {
  return apiClient.patch(`/payments/${id}/unvoid`).then(() => undefined);
}

/**
 * Refund a transaction by the given amount.
 */
export function processRefund(transactionId: number, amount: number): Promise<PaymentTransaction> {
  return apiClient
    .post<ApiResponse<PaymentTransaction>>(`/payments/refund/${transactionId}`, { amount })
    .then((r) => r.data.data);
}

export function createForwardPaymentIntent(data: {
  amount: number;
  currency?: string;
  description?: string;
  merchant_id?: number;
  patient_id?: number;
}): Promise<ForwardPaymentIntent> {
  return apiClient
    .post<ApiResponse<ForwardPaymentIntent>>('/forward/payment-intents', data)
    .then((r) => r.data.data);
}

// ─── Direct Payment Links (legacy parity) ─────────────────────────────────────

export interface PaymentLinkItem {
  merchantId: string;
  friendlyTitle: string;
  url: string;
}

export interface PaymentLinkDisclosure {
  disclosuresID: number;
  name: string;
}

export interface PaymentLinksResponse {
  gatewayType: string;
  accountName: string;
  disclosures: PaymentLinkDisclosure[];
  links: PaymentLinkItem[];
}

export function getPaymentLinks(
  patientId: number,
  planId?: number,
  disclosuresId?: number | string,
): Promise<PaymentLinksResponse> {
  const params: Record<string, string> = {};
  if (planId != null) params.planId = String(planId);
  if (disclosuresId != null && disclosuresId !== '') params.disclosuresId = String(disclosuresId);
  return apiClient
    .get<ApiResponse<PaymentLinksResponse>>(`/patients/${patientId}/payment-links`, { params })
    .then((r) => r.data.data);
}

/**
 * Response from the two-phase ad-hoc Charge Payment endpoint
 * (POST /v5/payments/{id}/charge-forward). Mirrors legacy
 * `payment_process_charge_authnet.php` — `success=false` means the gateway
 * declined the capture and the server has already voided the payment row
 * (Phase 2 side effects match legacy lines 445-753).
 */
export interface ForwardChargeResult {
  success: boolean;
  status: string;
  message: string;
  transactionId: string;
  intentId: string;
}

export function chargeForwardPayment(
  paymentId: number,
  data: {
    merchant_id: number;
    payment_method_id: string;
    amount: number;
    credit?: number;
    patient_id: number;
  }
): Promise<ForwardChargeResult> {
  return apiClient
    .post<ApiResponse<ForwardChargeResult>>(`/payments/${paymentId}/charge-forward`, data)
    .then((r) => r.data.data);
}

// ─── Credits ──────────────────────────────────────────────────────────────────

/**
 * List credit records. Optionally filter by patientId.
 */
export function listCredits(filters: { patientId?: number } = {}): Promise<Credit[]> {
  return apiClient
    .get<ApiResponse<Credit[]>>('/payments/credits', { params: filters })
    .then((r) => r.data.data);
}

/**
 * Create a new credit record for a patient.
 */
export function addCredit(data: {
  patientID:            number;
  creditAmount:         number;
  paymentsCreditTypeID?: number;
  paymentType?:         string;
}): Promise<Credit> {
  return apiClient
    .post<ApiResponse<Credit>>('/payments/credits', data)
    .then((r) => r.data.data);
}

// ─── Recurring schedules ──────────────────────────────────────────────────────

/**
 * Create a new recurring billing schedule.
 */
export function createRecurring(data: CreateRecurringPayload): Promise<RecurringSchedule> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/payments/recurring', data)
    .then((r) => mapRecurringSchedule(r.data.data ?? {}));
}

/**
 * Update an existing recurring billing schedule.
 */
export function updateRecurring(
  id: number,
  data: UpdateRecurringPayload,
): Promise<RecurringSchedule> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/payments/recurring/${id}`, data)
    .then((r) => mapRecurringSchedule(r.data.data ?? {}));
}

/**
 * Cancel (delete) a recurring billing schedule.
 */
export function cancelRecurring(id: number): Promise<void> {
  return apiClient.delete(`/payments/recurring/${id}`).then(() => undefined);
}

export function listRecurring(): Promise<RecurringSchedule[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/payments/recurring')
    .then((r) => (r.data.data ?? []).map((item) => mapRecurringSchedule(item ?? {})));
}

// ─── Billing methods ──────────────────────────────────────────────────────────

/**
 * List saved billing methods for a patient.
 */
export function listBillingMethods(patientId: number, includeDeleted = false): Promise<BillingMethod[]> {
  const params = includeDeleted ? { includeDeleted: '1' } : undefined;
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>(`/payments/billing-methods/${patientId}`, { params })
    .then((r) => (r.data.data ?? []).map((row) => mapBillingMethod(row)));
}

export function createPatientPayment(
  patientId: number,
  data: CreatePatientPaymentPayload,
): Promise<Payment> {
  return apiClient
    .post<ApiResponse<Payment>>(`/patients/${patientId}/payments`, data)
    .then((r) => r.data.data);
}

export function getPaymentDetail(id: number): Promise<PaymentDetail> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>>>(`/payments/${id}`)
    .then((r) => {
      const raw = r.data.data ?? {};
      const curAmount = asNullableNumber(raw.curAmount) ?? 0;
      const giftCardAppliedTotal = asNullableNumber(raw.giftCardAppliedTotal) ?? 0;
      return {
        ...mapPayment(raw),
        productTotal: asNullableNumber(raw.productTotal) ?? curAmount + giftCardAppliedTotal,
        grossAmount: asNullableNumber(raw.grossAmount) ?? curAmount + giftCardAppliedTotal,
        cardChargedAmount: asNullableNumber(raw.cardChargedAmount) ?? curAmount,
        giftCardAppliedTotal,
        curAmountIsGross: raw.curAmountIsGross === true,
        shipping: Array.isArray(raw.shipping)
          ? raw.shipping.map((row) => ({
              shippingFeeID: asNullableNumber((row as Record<string, unknown>).shippingFeeID) ?? 0,
              name: asNullableString((row as Record<string, unknown>).name) ?? '',
              amount: asNullableNumber((row as Record<string, unknown>).amount) ?? 0,
            }))
          : [],
        shippingTotal: asNullableNumber(raw.shippingTotal) ?? 0,
        transactions: Array.isArray(raw.transactions)
          ? raw.transactions.map((row) => ({
              paymentTransactionAuthnetID: asNullableNumber((row as Record<string, unknown>).paymentTransactionAuthnetID) ?? 0,
              paymentMethodToken: asNullableString((row as Record<string, unknown>).paymentMethodToken),
              transactionID: asNullableString((row as Record<string, unknown>).transactionID),
              status: asNullableString((row as Record<string, unknown>).status),
              amount: asNullableNumber((row as Record<string, unknown>).amount) ?? 0,
              createDate: asNullableString((row as Record<string, unknown>).createDate),
              accountAuthnetID: asNullableNumber((row as Record<string, unknown>).accountAuthnetID),
              merchantTitle: asNullableString((row as Record<string, unknown>).merchantTitle),
              declineReason: asNullableString((row as Record<string, unknown>).declineReason),
            }))
          : [],
        credits: Array.isArray(raw.credits)
          ? raw.credits.map((row) => ({
              paymentsCreditID: asNullableNumber((row as Record<string, unknown>).paymentsCreditID) ?? 0,
              serialNumber: asNullableString((row as Record<string, unknown>).serialNumber),
              creditAmount: asNullableNumber((row as Record<string, unknown>).creditAmount) ?? 0,
              redeemedAmount: asNullableNumber((row as Record<string, unknown>).redeemedAmount) ?? 0,
              creditTypeName: asNullableString((row as Record<string, unknown>).creditTypeName),
            }))
          : [],
        giftCards: Array.isArray(raw.giftCards)
          ? raw.giftCards.map((row) => ({
              giftCardLogID: asNullableNumber((row as Record<string, unknown>).giftCardLogID) ?? 0,
              giftCardID: asNullableNumber((row as Record<string, unknown>).giftCardID) ?? 0,
              serialNumber: asNullableString((row as Record<string, unknown>).serialNumber),
              title: asNullableString((row as Record<string, unknown>).title),
              amount: asNullableNumber((row as Record<string, unknown>).amount) ?? 0,
              balanceAmount: asNullableNumber((row as Record<string, unknown>).balanceAmount) ?? 0,
              redeemedAmount: asNullableNumber((row as Record<string, unknown>).redeemedAmount) ?? 0,
              createDate: asNullableString((row as Record<string, unknown>).createDate),
            }))
          : [],
        appliedGiftCards: Array.isArray(raw.appliedGiftCards)
          ? raw.appliedGiftCards.map((row) => ({
              giftCardID: asNullableNumber((row as Record<string, unknown>).giftCardID) ?? 0,
              amount: asNullableNumber((row as Record<string, unknown>).amount) ?? 0,
            }))
          : undefined,
        materials: Array.isArray(raw.materials)
          ? raw.materials.map((row) => ({
              paymentMaterialID: asNullableNumber((row as Record<string, unknown>).paymentMaterialID) ?? 0,
              itemId: asNullableNumber((row as Record<string, unknown>).itemId) ?? 0,
              name: asNullableString((row as Record<string, unknown>).name),
              description: asNullableString((row as Record<string, unknown>).description),
              price: asNullableNumber((row as Record<string, unknown>).price) ?? 0,
              taxable: Boolean((row as Record<string, unknown>).taxable),
              quantity: asNullableNumber((row as Record<string, unknown>).quantity) ?? 0,
              salesTax: asNullableNumber((row as Record<string, unknown>).salesTax) ?? 0,
              discount1Id: asNullableNumber((row as Record<string, unknown>).discount1Id),
              discount2Id: asNullableNumber((row as Record<string, unknown>).discount2Id),
              manualDiscount1: asNullableNumber((row as Record<string, unknown>).manualDiscount1) ?? 0,
              manualDiscount2: asNullableNumber((row as Record<string, unknown>).manualDiscount2) ?? 0,
              total: asNullableNumber((row as Record<string, unknown>).total) ?? 0,
              categoryTitle: asNullableString((row as Record<string, unknown>).categoryTitle),
              serialNumber: asNullableString((row as Record<string, unknown>).serialNumber),
              giftCardId: asNullableNumber((row as Record<string, unknown>).giftCardId),
              giftCardCodeMasked: (row as Record<string, unknown>).giftCardCodeMasked === true,
              giftCardClaimed: (row as Record<string, unknown>).giftCardClaimed === true,
              giftCardOwnerPatientId: asNullableNumber((row as Record<string, unknown>).giftCardOwnerPatientId),
              giftCardOwnerName: asNullableString((row as Record<string, unknown>).giftCardOwnerName),
            }))
          : [],
        loans: Array.isArray(raw.loans)
          ? raw.loans.map((row) => ({
              paymentLoanID: asNullableNumber((row as Record<string, unknown>).paymentLoanID) ?? 0,
              loanId: asNullableNumber((row as Record<string, unknown>).loanId) ?? 0,
              paymentAmount: asNullableNumber((row as Record<string, unknown>).paymentAmount) ?? 0,
              loanType: asNullableString((row as Record<string, unknown>).loanType),
              loanAmount: asNullableNumber((row as Record<string, unknown>).loanAmount) ?? 0,
              loanStatus: asNullableString((row as Record<string, unknown>).loanStatus),
              createDate: asNullableString((row as Record<string, unknown>).createDate),
            }))
          : [],
        packages: Array.isArray(raw.packages)
          ? raw.packages.map((row) => {
              const r = row as Record<string, unknown>;
              return {
                packageId: asNullableNumber(r.packageId) ?? 0,
                title: asNullableString(r.title),
                packageTotal: asNullableNumber(r.packageTotal) ?? 0,
                templatePrice: asNullableNumber(r.templatePrice) ?? 0,
                credit: asNullableNumber(r.credit) ?? 0,
                chargeAmount: asNullableNumber(r.chargeAmount) ?? 0,
                giftCardApplied: asNullableNumber(r.giftCardApplied) ?? 0,
                discount1Id: asNullableNumber(r.discount1Id),
                discount2Id: asNullableNumber(r.discount2Id),
                manualDiscount1: asNullableNumber(r.manualDiscount1) ?? 0,
                manualDiscount2: asNullableNumber(r.manualDiscount2) ?? 0,
                createDate: asNullableString(r.createDate),
                items: Array.isArray(r.items)
                  ? (r.items as Record<string, unknown>[]).map((item) => ({
                      itemName: asNullableString(item.itemName) ?? '',
                      quantity: asNullableNumber(item.quantity) ?? 1,
                      itemType: (asNullableString(item.itemType) ?? 'product') as 'product' | 'procedure',
                    }))
                  : [],
              };
            })
          : [],
        memberships: Array.isArray(raw.memberships)
          ? raw.memberships.map((row) => {
              const r = row as Record<string, unknown>;
              return {
                enrollmentId: asNullableNumber(r.enrollmentId) ?? 0,
                title: asNullableString(r.title),
                recurringAmount: asNullableNumber(r.recurringAmount) ?? 0,
                createDate: asNullableString(r.createDate),
                paymentDate: asNullableString(r.paymentDate),
                paymentUntilDate: asNullableString(r.paymentUntilDate),
              };
            })
          : [],
        promoCode: raw.promoCode
          ? (() => {
              const pc = raw.promoCode as Record<string, unknown>;
              return {
                promoCodeID: asNullableNumber(pc.promoCodeID) ?? 0,
                code: asNullableString(pc.code) ?? '',
                title: asNullableString(pc.title) ?? '',
                description: asNullableString(pc.description) ?? '',
                type: asNullableString(pc.type) ?? 'unlimited',
                discountTitle: asNullableString(pc.discountTitle) ?? '',
                discountType: asNullableNumber(pc.discountType) ?? 0,
                discountAmount: asNullableNumber(pc.discountAmount) ?? 0,
                appliedDiscount: asNullableNumber(pc.appliedDiscount) ?? 0,
              };
            })()
          : null,
      };
    });
}

export function listPaymentMethodOptions(): Promise<PaymentMethodOption[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/payments/method-options')
    .then((r) =>
      (r.data.data ?? []).map((row) => ({
        paymentMethodID: asNullableNumber(row.paymentMethodID) ?? 0,
        name: asNullableString(row.name) ?? '',
        pmEnumID: asNullableNumber(row.pmEnumID),
        typeIntentID: asNullableNumber(row.typeIntentID),
        active: Boolean(row.active),
        isCreditType: Boolean(row.isCreditType),
        surchargeType: row.surchargeType === 'percent'
          ? ('percent' as const)
          : row.surchargeType === 'flat'
            ? ('flat' as const)
            : null,
        surchargeAmount: asNullableNumber(row.surchargeAmount),
      })),
    );
}

/**
 * Save a new billing method (tokenised card) for a patient.
 * SECURITY: token must be a gateway-issued token. Raw card numbers must never be stored.
 */
export function saveBillingMethod(data: SaveBillingMethodPayload): Promise<BillingMethod> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/payments/billing-methods', data)
    .then((r) => mapBillingMethod(r.data.data ?? {}));
}

/**
 * Soft-delete a saved billing method (sets endDate, does not hard-delete).
 */
export function deleteBillingMethod(id: number): Promise<BillingMethod> {
  return apiClient
    .delete<ApiResponse<Record<string, unknown>>>(`/payments/billing-methods/${id}`)
    .then((r) => mapBillingMethod(r.data.data ?? {}));
}

/**
 * Restore a soft-deleted billing method (clears endDate).
 */
export function restoreBillingMethod(id: number): Promise<BillingMethod> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/payments/billing-methods/${id}/restore`)
    .then((r) => mapBillingMethod(r.data.data ?? {}));
}

/**
 * Set a billing method as the patient's default.
 * Returns the full updated list of active billing methods.
 */
export function setDefaultBillingMethod(id: number): Promise<BillingMethod[]> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>[]>>(`/payments/billing-methods/${id}/set-default`)
    .then((r) => (r.data.data ?? []).map((row) => mapBillingMethod(row)));
}

// ─── Quotes ───────────────────────────────────────────────────────────────────

/**
 * Create a new treatment quote with line items.
 */
export function createQuote(data: CreateQuotePayload): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>('/payments/quotes', data)
    .then((r) => r.data.data);
}

/**
 * Fetch a quote with its line items.
 */
export function getQuote(id: number): Promise<Quote> {
  return apiClient
    .get<ApiResponse<Quote>>(`/payments/quotes/${id}`)
    .then((r) => r.data.data);
}

/**
 * Sign a quote. Records the signer's name and transitions status to Signed.
 */
export function signQuote(id: number, signedByName: string): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>(`/payments/quotes/${id}/sign`, { signedByName })
    .then((r) => r.data.data);
}

// ─── Gift cards ───────────────────────────────────────────────────────────────

/**
 * Look up a gift card by serial number / code.
 * Returns null if the card is not found, voided, or has no remaining balance.
 */
export function searchGiftCard(code: string): Promise<GiftCard | null> {
  return apiClient
    .post<ApiResponse<GiftCard | null>>('/payments/gift-cards/search', { code })
    .then((r) => r.data.data);
}

export interface PaymentListStats {
  total: number;
  active: number;
  voided: number;
  /** Raw SUM(curAmount). Mixes stored-value sales with earned revenue — prefer the split below. */
  totalAmount: number;
  avgPayment: number;
  /** Money actually received: product payments + gift card sales, gift-card tenders excluded. */
  cashCollected?: number;
  /** Delivered products/services: cash − gift cards sold + gift cards redeemed. */
  revenueEarned?: number;
  /** Stored value sold in the filtered period (cash, but not yet revenue). */
  giftCardSold?: number;
  /** Stored value spent in the filtered period (revenue, but not new cash). */
  giftCardRedeemed?: number;
  /**
   * The slice of `cashCollected` taken in a patient app rather than entered by
   * staff — i.e. payments carrying an `api_request_metadata` row. Same per-row
   * expression as `cashCollected`, so it is a strict subset of it.
   */
  appPurchased?: number;
  /** Outstanding stored value right now — point-in-time, ignores the date filter. */
  giftCardLiability?: number;
}

export function getPaymentStats(params: PaymentFilters = {}): Promise<PaymentListStats> {
  return apiClient
    .get<{ data: PaymentListStats }>('/payments/stats', { params })
    .then((r) => r.data.data);
}

// ─── Merchants ────────────────────────────────────────────────────────────────

export interface Merchant {
  merchantId: number;
  friendlyTitle: string;
  isDefault: boolean;
  gatewayType: string;
  facilityId: number | null;
}

export function listMerchants(): Promise<Merchant[]> {
  return apiClient
    .get<ApiResponse<Merchant[]>>('/payments/merchants')
    .then((r) => r.data.data);
}

// ─── Discounts ────────────────────────────────────────────────────────────────

export interface PaymentDiscount {
  paymentDiscountID: number;
  title: string;
  discountType: number;
  discountAmount: number;
  active: boolean;
}

export function listDiscounts(): Promise<PaymentDiscount[]> {
  return apiClient
    .get<ApiResponse<PaymentDiscount[]>>('/payment-discounts?include_inactive')
    .then((r) => r.data.data);
}

export function createDiscount(
  data: Pick<PaymentDiscount, 'title' | 'discountType' | 'discountAmount'>,
): Promise<PaymentDiscount> {
  return apiClient
    .post<ApiResponse<PaymentDiscount>>('/payment-discounts', data)
    .then((r) => r.data.data);
}

export function updateDiscount(
  id: number,
  data: Partial<Pick<PaymentDiscount, 'title' | 'discountType' | 'discountAmount'>>,
): Promise<PaymentDiscount> {
  return apiClient
    .put<ApiResponse<PaymentDiscount>>(`/payment-discounts/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteDiscount(id: number): Promise<void> {
  return apiClient.delete(`/payment-discounts/${id}`).then(() => undefined);
}

export function restoreDiscount(id: number): Promise<void> {
  return apiClient.post(`/payment-discounts/${id}/restore`).then(() => undefined);
}

// ─── Shipping fees ────────────────────────────────────────────────────────────

/**
 * A reusable, per-account shipping fee. Attached to products (see
 * `ProductAddon.shippingFeeID`) and charged once per distinct fee per payment,
 * however many carrying products are on it. Never taxed and never discounted.
 */
export interface ShippingFee {
  shippingFeeID: number;
  name: string;
  amount: number;
  feeOrder: number;
  active: boolean;
}

/**
 * Read-only in Prospera: the payment modal needs the account's active fees for
 * the "Add shipping" picker. Creating, editing and deleting shipping fees — and
 * assigning them to products — lives in the corporate dashboard, not here.
 */
export function listShippingFees(): Promise<ShippingFee[]> {
  return apiClient
    .get<ApiResponse<ShippingFee[]>>('/shipping-fees')
    .then((r) => r.data.data ?? []);
}

// ─── Gift card extended operations ────────────────────────────────────────────

/**
 * Look up a typed gift card code. Pass the patient paying so the API applies
 * the owner rule: another patient's card answers 409
 * GIFT_CARD_OWNED_BY_OTHER_PATIENT, an unknown code (or another location's)
 * 404, and too many wrong codes 429 GIFT_CARD_CODE_LOCKED — see
 * giftCardCodeErrorMessage in lib/giftCards.
 */
export function checkGiftCardBalance(serialNumber: string, patientId?: number): Promise<GiftCard | null> {
  return apiClient
    .get<ApiResponse<GiftCard | null>>(`/gift-cards/check/${encodeURIComponent(serialNumber)}`, {
      params: patientId && patientId > 0 ? { patientId } : undefined,
    })
    .then((r) => {
      const raw = r.data.data as Record<string, unknown> | null;
      if (!raw) return null;

      const asNumber = (value: unknown, fallback = 0): number => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
          return Number(value);
        }
        return fallback;
      };

      const asString = (value: unknown, fallback = ''): string =>
        typeof value === 'string' ? value : fallback;

      const asNullableString = (value: unknown): string | null =>
        typeof value === 'string' && value !== '' ? value : null;

      const giftCardId = asNumber(raw.giftCardId ?? raw.giftCardID);
      const currentBalance = asNumber(raw.currentBalance ?? raw.balanceAmount);
      const originalValue = asNumber(raw.originalValue ?? raw.purchaseAmount);

      return {
        giftCardId,
        paymentsCreditID: giftCardId,
        accountID: asNumber(raw.accountId ?? raw.accountID),
        serialNumber: asString(raw.serialNumber ?? raw.giftCardRefID),
        creditAmount: originalValue,
        redeemedAmount: asNumber(raw.redeemedAmount ?? Math.max(originalValue - currentBalance, 0)),
        remainingBalance: currentBalance,
        currentBalance,
        title: typeof raw.title === 'string' ? raw.title : null,
        void: currentBalance > 0 ? '0' : '1',
        createDate: asNullableString(raw.createdAt ?? raw.createDate ?? raw.purchaseDate),
        creditTypeName: typeof raw.title === 'string' ? raw.title : null,
      };
    });
}

export function redeemGiftCard(
  id: number,
  data: { amount: number; paymentId?: number },
): Promise<unknown> {
  return apiClient
    .post<ApiResponse<unknown>>(`/gift-cards/${id}/redeem`, data)
    .then((r) => r.data.data);
}

// ─── Patient-owned gift cards (legacy: router.php?obj=gift_cards&calAction=GetByField) ─
// Returns every open-balance gift card the patient owns. Mirrors `checkForGiftCards()`
// in legacy `gift-credit-handeling2.js`, which auto-attaches these on modal open.

interface RawPatientGiftCard {
  giftCardId: number;
  serialNumber: string;
  balanceAmount: number;
  redeemedAmount?: number;
  totalValue?: number;
  startDate?: string | null;
  endDate?: string | null;
  title?: string | null;
}

export function listPatientGiftCards(patientId: number): Promise<GiftCard[]> {
  return apiClient
    .get<ApiResponse<RawPatientGiftCard[]>>(`/patients/${patientId}/gift-cards`)
    .then((r) => {
      const rows = r.data.data ?? [];
      return rows.map((row) => {
        const bal = Number(row.balanceAmount) || 0;
        const redeemed = Number(row.redeemedAmount) || 0;
        const total = Number(row.totalValue) || (bal + redeemed);
        return {
          giftCardId: row.giftCardId,
          paymentsCreditID: row.giftCardId,
          accountID: 0,
          serialNumber: row.serialNumber ?? '',
          creditAmount: total,
          redeemedAmount: redeemed,
          remainingBalance: bal,
          currentBalance: bal,
          totalValue: total,
          title: row.title ?? null,
          void: '0',
          createDate: row.startDate ?? null,
          creditTypeName: row.title ?? null,
        } satisfies GiftCard;
      });
    });
}

// ─── Quote items catalog (products picker) ────────────────────────────────────

export interface QuoteItemCategory {
  categoryId: number;
  title: string;
}

export interface QuoteItem {
  itemId: number;
  name: string;
  description: string;
  unit: string;
  price: number;
  taxable: boolean;
  productType: number;
  categoryId: number | null;
  categoryTitle: string | null;
  memberPrice: number | null;
  /** When true, staff may edit this product's price at point-of-charge (set per-account in the dashboard). */
  allowPriceOverride: boolean;
  /**
   * Shipping fee this product carries, or null for none (also null when the fee
   * has been retired). Each distinct fee is charged once per payment regardless
   * of quantity or how many carrying products are on it.
   */
  shippingFee: { id: number; name: string; amount: number } | null;
}

export function listQuoteItemCategories(): Promise<QuoteItemCategory[]> {
  return apiClient
    .get<ApiResponse<QuoteItemCategory[]>>('/payments/quote-item-categories')
    .then((r) => r.data.data ?? []);
}

export function listQuoteItems(
  filters: { categoryId?: number; search?: string; limit?: number } = {},
): Promise<QuoteItem[]> {
  return apiClient
    .get<ApiResponse<QuoteItem[]>>('/payments/quote-items', { params: filters })
    .then((r) => r.data.data ?? []);
}

// ─── Per-payment product operations ────────────────────────────────────────────

export interface PaymentProductAvailable {
  paymentMaterialID: number;
  itemId: number;
  name: string;
  quantity: number;
  total: number;
  addedDate: string | null;
  expireDate: string | null;
  categoryTitle: string | null;
}

export interface PaymentProductUsed {
  paymentMaterialID: number;
  itemId: number;
  name: string;
  quantity: number;
  total: number;
  usedDate: string | null;
  deleteDate: string | null;
  deleteUserID: number | null;
}

export function listPaymentProductsAvailable(paymentId: number): Promise<PaymentProductAvailable[]> {
  return apiClient
    .get<ApiResponse<PaymentProductAvailable[]>>(`/payments/${paymentId}/products-available`)
    .then((r) => r.data.data ?? []);
}

export function listPaymentProductsUsed(paymentId: number): Promise<PaymentProductUsed[]> {
  return apiClient
    .get<ApiResponse<PaymentProductUsed[]>>(`/payments/${paymentId}/products-used`)
    .then((r) => r.data.data ?? []);
}

export function returnPaymentProduct(paymentId: number, lineId: number): Promise<void> {
  return apiClient
    .post(`/payments/${paymentId}/products/${lineId}/return`)
    .then(() => undefined);
}

export function deletePaymentProduct(paymentId: number, lineId: number): Promise<void> {
  return apiClient
    .delete(`/payments/${paymentId}/products/${lineId}`)
    .then(() => undefined);
}

export function disburseAllPaymentProducts(paymentId: number): Promise<{ disbursed: number }> {
  return apiClient
    .post<ApiResponse<{ disbursed: number }>>(`/payments/${paymentId}/disburse-all`)
    .then((r) => r.data.data);
}

// ─── Convert-to-credit ─────────────────────────────────────────────────────────

export interface ConvertToCreditPayload {
  amount?: number;
  creditTypeId?: number;
  notes?: string;
}

export interface ConvertToCreditResult {
  creditId: number;
  amount: number;
}

export function convertPaymentToCredit(
  paymentId: number,
  data: ConvertToCreditPayload = {},
): Promise<ConvertToCreditResult> {
  return apiClient
    .post<ApiResponse<ConvertToCreditResult>>(`/payments/${paymentId}/convert-to-credit`, data)
    .then((r) => r.data.data);
}

// ─── Update encounter ─────────────────────────────────────────────────────────

export interface UpdateEncounterPayload {
  planID?: number;
  quoteID?: number;
  facilityID?: number;
}

export function updatePaymentEncounter(
  paymentId: number,
  data: UpdateEncounterPayload,
): Promise<{ updated: boolean }> {
  return apiClient
    .post<ApiResponse<{ updated: boolean }>>(`/payments/${paymentId}/encounter`, data)
    .then((r) => r.data.data);
}

// ─── Refund (corrected to write to payment_transactions_authnet) ─────────────

export interface RefundPaymentPayload {
  amount?: number;
  reason?: string;
}

export interface RefundResult {
  refundTransactionId: number;
  refundAmount: number;
}

export function refundPaymentTransaction(
  transactionId: number,
  data: RefundPaymentPayload = {},
): Promise<RefundResult> {
  return apiClient
    .post<ApiResponse<RefundResult>>(`/payments/refund/${transactionId}`, data)
    .then((r) => r.data.data);
}

// ─── Receipts (print / email / sign / direct-link) ─────────────────────────────

export interface ReceiptTemplate {
  templateId: number;
  title: string;
  witnessSignature: boolean;
  signatureLine: boolean;
}

export interface EmailTemplate {
  templateId: number;
  title: string;
  subject: string;
  categoryId: number | null;
  categoryTitle: string | null;
}

export function listReceiptTemplates(): Promise<ReceiptTemplate[]> {
  return apiClient
    .get<ApiResponse<ReceiptTemplate[]>>('/payments/receipt-templates')
    .then((r) => r.data.data ?? []);
}

export function listEmailTemplates(): Promise<EmailTemplate[]> {
  return apiClient
    .get<ApiResponse<EmailTemplate[]>>('/payments/email-templates')
    .then((r) => r.data.data ?? []);
}

/**
 * Fetch the receipt PDF as a Blob. Deliberately returns the Blob and NOT an
 * object URL: this is consumed through a cached query, and an object URL is
 * only valid until whoever minted it revokes it. Caching the URL string meant
 * a re-open got back an already-revoked URL and rendered a blank iframe. The
 * caller mints (and revokes) its own URL from this Blob per mount.
 */
export async function previewReceiptPdfBlob(
  paymentId: number,
  templateId?: number,
): Promise<Blob> {
  const r = await apiClient.get<Blob>(`/payments/${paymentId}/receipt/preview`, {
    params: templateId ? { templateId } : {},
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  });
  return r.data;
}

/**
 * Trigger a browser download of the receipt PDF (Print). Opens in a new tab
 * so the browser's PDF viewer can handle print/save.
 */
export async function downloadReceiptPdf(
  paymentId: number,
  templateId?: number,
): Promise<void> {
  const r = await apiClient.get<Blob>(`/payments/${paymentId}/receipt/download`, {
    params: templateId ? { templateId } : {},
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  });
  const blobUrl = URL.createObjectURL(r.data);
  const win = window.open(blobUrl, '_blank');
  // Revoke after the new window has had a chance to load
  if (win) {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } else {
    URL.revokeObjectURL(blobUrl);
  }
}

export interface EmailReceiptPayload {
  receiptTemplateId: number;
  subject?: string;
}

export interface EmailReceiptResult {
  sent: boolean;
  commLogId: number;
  toEmail: string;
  mandrillId: string;
}

export function emailReceipt(
  paymentId: number,
  data: EmailReceiptPayload,
): Promise<EmailReceiptResult> {
  return apiClient
    .post<ApiResponse<EmailReceiptResult>>(
      `/payments/${paymentId}/receipt/email`,
      data,
    )
    .then((r) => r.data.data);
}

export interface ReceiptEmailPreview {
  html: string;
  defaultSubject: string;
}

/**
 * Fetch the rendered HTML body + the default subject the email send
 * would use. The modal iframes the HTML via `srcDoc` and pre-fills the
 * subject input with `defaultSubject`.
 */
export async function previewReceiptEmailHtml(
  paymentId: number,
  receiptTemplateId: number,
): Promise<ReceiptEmailPreview> {
  return apiClient
    .get<ApiResponse<ReceiptEmailPreview>>(
      `/payments/${paymentId}/receipt/email/preview`,
      { params: { templateId: receiptTemplateId } },
    )
    .then((r) => r.data.data);
}

export interface SignReceiptPayload {
  receiptTemplateId: number;
  signatureData: string;
  witnessSignatureData?: string;
  signedByName?: string;
}

/**
 * Submit drawn signature(s) and receive the signed receipt PDF (with
 * signature(s) embedded). Returns an object URL the UI can embed.
 */
export async function signReceiptPdfUrl(
  paymentId: number,
  data: SignReceiptPayload,
): Promise<string> {
  const r = await apiClient.post<Blob>(
    `/payments/${paymentId}/receipt/sign`,
    data,
    {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
    },
  );
  return URL.createObjectURL(r.data);
}

export interface ReceiptDirectLink {
  fullUrl: string;
  shortUrl: string;
  filename: string;
}

export function receiptDirectLink(
  paymentId: number,
  receiptTemplateId: number,
): Promise<ReceiptDirectLink> {
  return apiClient
    .post<ApiResponse<ReceiptDirectLink>>(
      `/payments/${paymentId}/receipt/direct-link`,
      { receiptTemplateId },
    )
    .then((r) => r.data.data);
}

// ─── Forward terminal (EMV) ────────────────────────────────────────────────────

export interface PaymentTerminal {
  terminalForwardId: number;
  title: string;
  hsn: string;
  accountForwardId: number;
  merchantTitle: string;
  merchantIsDefault: boolean;
}

export interface TerminalTokenizeResult {
  success: boolean;
  token: string | null;
  masked_pan: string;
  card_brand: string;
  card_type: string;
  exp_month: string;
  exp_year: string;
  is_reusable: boolean;
  /**
   * Non-fatal warning when token capture succeeded but the saved-card row
   * could not be inserted. UI should display prominently.
   */
  save_warning: string | null;
  terminalForwardId: number;
  accountForwardId: number;
}

export interface TerminalCheckoutResult {
  success: boolean;
  payment_intent_id: string | null;
  transaction_id: string | null;
  auth_code: string | null;
  status: string;
  token: string | null;
  masked_pan: string;
  card_brand: string;
  reuse_eligible: boolean;
  amount: number;
  /**
   * Reconciliation log row id. Must be POSTed to
   * `/payments/terminals/transactions/{logId}/mark-complete` after the
   * follow-up createPatientPayment commits. If null or the mark-complete
   * call is skipped, the row is closed once the charge is found recorded;
   * nothing reverses the charge.
   */
  transaction_log_id: number | null;
  /**
   * Non-fatal warning when the card was charged successfully but the
   * saved-card row could not be inserted (e.g. DB connection failure).
   * UI should display prominently — charge still went through.
   */
  save_warning: string | null;
  terminalForwardId: number;
  accountForwardId: number;
}

export function listPaymentTerminals(): Promise<PaymentTerminal[]> {
  return apiClient
    .get<ApiResponse<PaymentTerminal[]>>('/payments/terminals')
    .then((r) => r.data.data ?? []);
}

export function getTerminalDetails(id: number): Promise<unknown> {
  return apiClient
    .get<ApiResponse<unknown>>(`/payments/terminals/${id}/details`)
    .then((r) => r.data.data);
}

export function testTerminalConnection(id: number): Promise<{ success: boolean; message?: string; hsn?: string }> {
  return apiClient
    .post<ApiResponse<{ success: boolean; message?: string; hsn?: string }>>(`/payments/terminals/${id}/test-connection`)
    .then((r) => r.data.data);
}

export function clearTerminalSession(id: number): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiClient
    .post<ApiResponse<{ success: boolean; message?: string; error?: string }>>(`/payments/terminals/${id}/clear-session`)
    .then((r) => r.data.data);
}

export function tokenizeCardOnTerminal(
  id: number,
  payload: { patientId?: number; amount?: number } = {},
): Promise<TerminalTokenizeResult> {
  return apiClient
    .post<ApiResponse<TerminalTokenizeResult>>(`/payments/terminals/${id}/tokenize`, payload)
    .then((r) => r.data.data);
}

/**
 * `attemptKey` must stay THE SAME across retries of the same logical charge. The
 * server passes it to Forward as an idempotency key, so a retry after a timeout
 * reuses the original payment intent instead of creating a second one — which is
 * what stops "the terminal didn't respond" from turning into a double charge.
 * Generate a fresh key only for a genuinely new sale.
 */
export function checkoutOnTerminal(
  id: number,
  payload: {
    patientId?: number;
    amount: number;
    aid?: 'debit' | 'credit';
    attemptKey?: string;
  },
): Promise<TerminalCheckoutResult> {
  return apiClient
    .post<ApiResponse<TerminalCheckoutResult>>(`/payments/terminals/${id}/checkout`, payload)
    .then((r) => r.data.data);
}

/**
 * What the card processor says about a reader charge whose checkout request
 * timed out or could not confirm it. `captured` and `recorded` both carry the
 * checkout's own success shape, so the charge is saved exactly like a normal
 * one; for `recorded` a payment already exists, and the save completes or
 * reuses it rather than adding a second.
 */
export type TerminalCheckoutStatus =
  | { status: 'captured'; charge: TerminalCheckoutResult }
  | { status: 'recorded'; paymentId: number; charge: TerminalCheckoutResult }
  | { status: 'not_captured' }
  | { status: 'pending' };

/** Ask about the charge started with this `attemptKey` on this terminal. */
export function checkTerminalCheckoutStatus(id: number, attemptKey: string): Promise<TerminalCheckoutStatus> {
  return apiClient
    .post<ApiResponse<TerminalCheckoutStatus>>(`/payments/terminals/${id}/checkout-status`, { attemptKey })
    .then((r) => r.data.data);
}

/**
 * Mirrors legacy `markComplete` GET in payments-test.js — fire-and-forget
 * acknowledgement that the browser successfully recorded the terminal charge
 * via createPatientPayment. Flips `terminal_transaction_log.browser_callback_completed`
 * so the row reads as finished. If this call fails or never fires, the row is
 * closed once the charge is found recorded; nothing reverses the charge.
 */
export function markTerminalTransactionComplete(
  logId: number,
  paymentId?: number,
): Promise<{ success: boolean }> {
  return apiClient
    .post<ApiResponse<{ success: boolean }>>(
      `/payments/terminals/transactions/${logId}/mark-complete`,
      { paymentId: paymentId ?? null },
    )
    .then((r) => r.data.data);
}

// ─── Billing method update (billing address) ──────────────────────────────────

export interface UpdateBillingMethodPayload {
  title?: string;
  zipcode?: string;
  expireDate?: string;
  lastfour?: string;
  firstsix?: string;
}

export function updateBillingMethod(
  id: number,
  data: UpdateBillingMethodPayload,
): Promise<BillingMethod> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/payments/billing-methods/${id}`, data)
    .then((r) => mapBillingMethod(r.data.data ?? {}));
}

// ── App Orders (SCRUM-1392) ─────────────────────────────────────────────────

/**
 * One payment that bought a product, with the payments-side category of what
 * was bought and which client took the payment.
 */
export interface AppOrderRow {
  paymentID: number;
  patientID: number;
  patientName: string | null;
  dtmDate: string | null;
  amount: number;
  payType: string | null;
  payMethod: string | null;
  void: boolean;
  /**
   * The same raw `payments_list_index.paymentStatus` value the Payments list
   * exposes as `Payment.transStatus` — feed it through
   * `getPaymentStatusDisplay()`/`PaymentStatusBadge` for a label, exactly as
   * that list does, so "declined" and "refunded" stop looking identical here.
   */
  transStatus: string | null;
  planID: number | null;
  planDisplayID: string | null;
  facilityID: number | null;
  facilityName: string | null;
  /** Product line items on the payment, comma-joined in line order. */
  products: string | null;
  /** Distinct categories those products belong to, comma-joined. */
  categories: string | null;
  /**
   * Which client took the payment: 'webapp' | 'mobile' | 'nexus'.
   *
   * Read from `api_request_metadata` (SCRUM-1546): platform 'ios'/'android' is
   * mobile, anything else the apps send is the patient web app.
   *
   * NULL means UNTAGGED, never 'webapp' — every payment taken before that
   * capture shipped has no row, which is the overwhelming majority. Render it
   * as an em dash; never label an untagged payment with a channel.
   */
  source: string | null;
  /**
   * Whether this payment bought peptides — either an Axiom-family catalog item
   * or a line in a peptide-named category. Drives the Fulfill action.
   */
  isPeptide: boolean;
  /**
   * What the payment actually bought, verbatim. Shown beside the Axiom catalog
   * when fulfilling so staff can match by eye. It is deliberately NOT used to
   * preselect: nothing maps a practice catalog item to an Axiom product (only
   * ~3% of peptide line names match one exactly), and these are prescriptions.
   */
  paidItems: AppOrderPaidItem[];
  /**
   * The peptide order this payment paid for (SUP-000744), or null. Linked by
   * the payment being SPENT on the order's charge ("link an existing payment").
   * `live` is false when that order was cancelled — the payment then needs
   * fulfilling again.
   */
  fulfillment?: AppOrderFulfillment | null;
}

export interface AppOrderFulfillment {
  orderId: number;
  status: string | null;
  live: boolean;
}

export interface AppOrderPaidItem {
  name: string | null;
  quantity: number;
  amount: number;
  category: string | null;
}

export interface AppOrderSourceCounts {
  webapp: number;
  /** iOS and Android are counted apart: different stores, different releases. */
  ios: number;
  android: number;
  nexus: number;
  total: number;
}

export interface AppOrdersResponse {
  data: AppOrderRow[];
  meta: {
    /** NULL on a page turn — only recomputed when `withCounts` is sent. */
    total: number | null;
    page: number;
    perPage: number;
    sourceCounts: AppOrderSourceCounts | null;
  };
}

export interface AppOrdersParams {
  page?: number;
  perPage?: number;
  search?: string;
  /**
   * Keep rows whose basket contains a line in ANY of these categories. Omit
   * for every category.
   *
   * The catalog is overwhelmingly services — IV therapy, vitamin shots,
   * injectables, labs, memberships — so an unfiltered grid buries the product
   * purchases this page is for. The UI defaults to the account's peptide
   * categories; see AppOrdersPage.
   */
  categoryIds?: number[];
  /**
   * Keep rows whose source is ANY of these buckets ('webapp' | 'mobile' |
   * 'nexus' | 'untagged'). Omit for every source.
   *
   * Multi-valued because "bought in an app" spans several buckets and the
   * single-choice version made that three separate passes. The singular
   * `source` is still accepted by the API for old bookmarked URLs, but new
   * callers should send this.
   */
  sources?: string[];
  facilityIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  /**
   * Same vocabulary as the Payments list's status filter: 'manually_entered'
   * | 'pending' | 'settled' | 'voided' | 'declined' | 'refund'. Omit for
   * every status.
   */
  statuses?: string[];
  /**
   * 'needs' = a peptide payment (not void, for money) with no live order yet;
   * 'fulfilled' = one a live peptide order was paid with. Omit for both.
   */
  fulfillment?: 'needs' | 'fulfilled';
  /**
   * Recompute the row total and the source split. Costs a grouped scan over
   * every matching payment (~1.5s on the busiest account), and NONE of those
   * figures change when you turn a page — so send it on first load and on a
   * filter change, and leave it off while paging.
   */
  withCounts?: boolean;
}

export async function getAppOrders(params: AppOrdersParams = {}): Promise<AppOrdersResponse> {
  const r = await apiClient.get<ApiResponse<AppOrderRow[]>>('/payments/app-orders', { params });
  return {
    data: r.data.data ?? [],
    meta: (r.data as any).meta ?? { total: null, page: 1, perPage: 50, sourceCounts: null },
  };
}

/** A practice's product categories — the vocabulary the Category filter uses. */
export interface ProductCategoryOption {
  quoteItemCostsCategoryID: number;
  title: string;
}

export async function listProductCategories(): Promise<ProductCategoryOption[]> {
  const r = await apiClient.get<ApiResponse<ProductCategoryOption[]>>('/product-categories');
  return r.data.data ?? [];
}

// ─── Payouts ─────────────────────────────────────────────────────────────────

/**
 * A Forward payout — one deposit into the practice's bank account.
 *
 * Read from the local archive rather than from Forward directly, so these come
 * back at ordinary list speed. Amounts arrive as dollars; the API does the
 * conversion from Forward's integer cents.
 */
export interface PayoutRow {
  /** Forward's own id (po_...). The key for the transactions call. */
  id: string;
  merchantId: number;
  merchantName: string;
  payoutNumber: number;
  /** Raw Forward status, for styling. Use statusLabel for anything shown. */
  status: string;
  statusLabel: string;
  settlementStatus: string;
  currency: string;
  amount: number;
  netAmount: number;
  feeAmount: number;
  transactionCount: number;
  /**
   * False while a settled payout's breakdown is still being archived. The row
   * can still be expanded, but it should say the breakdown is not ready rather
   * than show a short list as if it were complete.
   */
  detailsReady: boolean;
  /** Pacific date of the deposit. */
  date: string | null;
  completedAt: string | null;
  createdAt: string | null;
}

export interface PayoutTransactionRow {
  id: string;
  type: string;
  amount: number;
  currency: string;
  description: string;
  settlementStatus: string;
  settledAt: string | null;
  date: string | null;
  reference: string;
}

export interface PayoutsMeta {
  total: number;
  page: number;
  perPage: number;
  totals: {
    amount: number;
    netAmount: number;
    feeAmount: number;
    transactionCount: number;
  };
}

export interface PayoutTransactionsMeta {
  total: number;
  page: number;
  perPage: number;
  /** Forward's own count. Above `total` means the archive is still filling in. */
  expectedTotal: number;
  complete: boolean;
}

export interface PayoutsResponse {
  data: PayoutRow[];
  meta: PayoutsMeta;
}

export interface PayoutTransactionsResponse {
  data: PayoutTransactionRow[];
  meta: PayoutTransactionsMeta;
}

export interface PayoutsParams {
  dateStart?: string;
  dateEnd?: string;
  status?: string;
  merchantIds?: number[];
  search?: string;
  page?: number;
  perPage?: number;
}

const EMPTY_PAYOUTS_META: PayoutsMeta = {
  total: 0,
  page: 1,
  perPage: 50,
  totals: { amount: 0, netAmount: 0, feeAmount: 0, transactionCount: 0 },
};

export async function getPayouts(params: PayoutsParams = {}): Promise<PayoutsResponse> {
  const r = await apiClient.get<ApiResponse<PayoutRow[]>>('/payments/payouts', { params });
  return {
    data: r.data.data ?? [],
    meta: (r.data as any).meta ?? EMPTY_PAYOUTS_META,
  };
}

/** The transactions inside one payout. Fetched only when a row is expanded. */
export async function getPayoutTransactions(
  payoutId: string,
  params: { page?: number; perPage?: number } = {},
): Promise<PayoutTransactionsResponse> {
  const r = await apiClient.get<ApiResponse<PayoutTransactionRow[]>>(
    `/payments/payouts/${encodeURIComponent(payoutId)}/transactions`,
    { params },
  );
  return {
    data: r.data.data ?? [],
    meta: (r.data as any).meta ?? { total: 0, page: 1, perPage: 50, expectedTotal: 0, complete: false },
  };
}

// ─── Disputes ────────────────────────────────────────────────────────────────

/**
 * A Forward dispute — one chargeback raised against a payment.
 *
 * Read from the local archive rather than from Forward directly, so these come
 * back at ordinary list speed. Amounts arrive as dollars; the API does the
 * conversion from Forward's integer cents.
 */
export interface DisputeRow {
  /** Forward's own id (dp_...). The key for the detail call. */
  id: string;
  merchantId: number;
  merchantName: string;
  /** Raw Forward status, for styling. Use statusLabel for anything shown. */
  status: string;
  statusLabel: string;
  currency: string;
  amount: number;
  reason: string;
  reasonCode: number;
  cardLastFour: string;
  disputeDate: string | null;
  /** The day a response is owed by. Null once the dispute is closed. */
  responseDueDate: string | null;
  closedDate: string | null;
  noteCount: number;
  attachmentCount: number;
  /**
   * The patient and payment in THIS system, matched through the payment intent.
   * 0 means the disputed charge has no record here, which is normal for a
   * payment taken elsewhere — the row shows a dash rather than a broken link.
   */
  patientId: number;
  patientName: string;
  paymentId: number;
}

export interface DisputeNote {
  id: string;
  message: string;
  date: string;
}

export interface DisputeAttachment {
  id: string;
  description: string;
  date: string;
}

/** Everything behind one expanded dispute. Fetched only when a row is opened. */
export interface DisputeDetail {
  id: string;
  status: string;
  statusLabel: string;
  currency: string;
  amount: number;
  reason: string;
  reasonCode: number;
  /** The practice's own response, as recorded by Forward. */
  response: string;
  disputeDate: string | null;
  receivedDate: string | null;
  responseDueDate: string | null;
  originalResponseDueDate: string | null;
  closedDate: string | null;
  decidedDate: string | null;
  settlementStatus: string;
  cardLastFour: string;
  authorizationCode: string;
  retrievalReference: string;
  transactionDate: string;
  transactionAmount: number;
  merchantName: string;
  patientId: number;
  patientName: string;
  paymentId: number;
  notes: DisputeNote[];
  attachments: DisputeAttachment[];
}

export interface DisputesMeta {
  total: number;
  page: number;
  perPage: number;
  totals: {
    amount: number;
    needsResponse: number;
    open: number;
    won: number;
    lost: number;
  };
}

export interface DisputesResponse {
  data: DisputeRow[];
  meta: DisputesMeta;
}

export interface DisputesParams {
  dateStart?: string;
  dateEnd?: string;
  /** A Forward status, or 'open' for everything still live. */
  status?: string;
  merchantIds?: number[];
  search?: string;
  page?: number;
  perPage?: number;
}

const EMPTY_DISPUTES_META: DisputesMeta = {
  total: 0,
  page: 1,
  perPage: 50,
  totals: { amount: 0, needsResponse: 0, open: 0, won: 0, lost: 0 },
};

export async function getDisputes(params: DisputesParams = {}): Promise<DisputesResponse> {
  const r = await apiClient.get<ApiResponse<DisputeRow[]>>('/payments/disputes', { params });
  return {
    data: r.data.data ?? [],
    meta: (r.data as any).meta ?? EMPTY_DISPUTES_META,
  };
}

/** One dispute in full. Fetched only when a row is expanded. */
export async function getDispute(disputeId: string): Promise<DisputeDetail | null> {
  const r = await apiClient.get<ApiResponse<DisputeDetail>>(
    `/payments/disputes/${encodeURIComponent(disputeId)}`,
  );
  return r.data.data ?? null;
}
